import { Component, inject, signal, ChangeDetectionStrategy, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { AuthService } from '../../core/services/auth';
import { UserService, UpdateMePayload, ChangePasswordPayload } from '../../core/services/user';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive, ReactiveFormsModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private userService = inject(UserService);
  private fb = inject(FormBuilder);
  private platformId = inject(PLATFORM_ID);

  /** Below this width (px) sidebar is forced collapsed */
  private static readonly SIDEBAR_COLLAPSE_BREAKPOINT = 1024;

  isCollapsed = signal(false);
  isProfileOpen = signal(false);
  profileClosing = signal(false);
  private profileCloseTimer: ReturnType<typeof setTimeout> | null = null;

  private mediaQueryList: MediaQueryList | null = null;
  private mediaListener: (() => void) | null = null;

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const query = `(max-width: ${SidebarComponent.SIDEBAR_COLLAPSE_BREAKPOINT - 1}px)`;
    this.mediaQueryList = window.matchMedia(query);
    const update = () => {
      if (this.mediaQueryList?.matches) {
        this.isCollapsed.set(true);
      } else {
        this.isCollapsed.set(false);
      }
    };
    update();
    this.mediaListener = update;
    this.mediaQueryList.addEventListener('change', this.mediaListener);
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId) && this.mediaQueryList && this.mediaListener) {
      this.mediaQueryList.removeEventListener('change', this.mediaListener);
    }
    this.clearProfileCloseTimer();
  }

  toggleSidebar() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.mediaQueryList?.matches) return;
    this.isCollapsed.update(v => !v);
  }

  showPasswordChange = signal(false);
  saving = signal(false);
  photoUploading = signal(false);
  saveError = signal<string | null>(null);
  saveSuccess = signal(false);
  passwordError = signal<string | null>(null);
  passwordSuccess = signal(false);

  profileForm = this.fb.group({
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
  });

  passwordForm = this.fb.group({
    current_password: ['', Validators.required],
    new_password: ['', [Validators.required, Validators.minLength(8)]],
    confirm_password: ['', Validators.required],
  });

  logout() {
    this.auth.logout();
  }

  openProfile() {
    this.clearProfileCloseTimer();
    this.profileClosing.set(false);
    const user = this.auth.currentUser();
    this.profileForm.patchValue({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    });
    this.saveError.set(null);
    this.saveSuccess.set(false);
    this.passwordError.set(null);
    this.passwordSuccess.set(false);
    this.showPasswordChange.set(false);
    this.passwordForm.reset();
    this.isProfileOpen.set(true);
  }

  closeProfile() {
    if (!this.isProfileOpen() || this.profileClosing()) return;
    this.profileClosing.set(true);
    this.profileCloseTimer = setTimeout(() => {
      this.isProfileOpen.set(false);
      this.profileClosing.set(false);
      this.profileCloseTimer = null;
    }, 200);
  }

  private clearProfileCloseTimer() {
    if (!this.profileCloseTimer) return;
    clearTimeout(this.profileCloseTimer);
    this.profileCloseTimer = null;
  }

  /** Human-readable label for role key (e.g. super_admin → System Admin) */
  readonly roleLabels: Record<string, string> = {
    super_admin: 'System Admin',
    admin: 'Admin',
    content_creator: 'Editor',
    owner: 'Owner',
    editor: 'Editor',
    member: 'Member',
    user: 'User',
  };

  /** Current user's role label (from roles array or role field) */
  currentUserRoleLabel(): string {
    const user = this.auth.currentUser();
    const roles = user?.roles as string[] | undefined;
    const single = user?.role as string | undefined;
    const raw = Array.isArray(roles) && roles.length > 0 ? roles[0] : single;
    return (raw && this.roleLabels[raw]) || (raw ? raw.replace(/_/g, ' ') : '—');
  }

  /** Whether form values differ from current user (enables save button) */
  hasProfileChanges(): boolean {
    const user = this.auth.currentUser();
    const v = this.profileForm.value;
    return (v.first_name?.trim() !== (user?.first_name ?? '')) || (v.last_name?.trim() !== (user?.last_name ?? ''));
  }

  triggerPhotoInput(input: HTMLInputElement) {
    input.click();
  }

  onPhotoChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const maxBytes = 2 * 1024 * 1024; // 2 MB
    if (file.size > maxBytes) {
      this.saveError.set('Photo must be 2 MB or smaller.');
      input.value = '';
      return;
    }

    this.photoUploading.set(true);
    this.saveError.set(null);

    this.userService.uploadPhoto(file).subscribe({
      next: (user) => {
        this.auth.currentUser.set(user);
        this.photoUploading.set(false);
      },
      error: (err) => {
        this.saveError.set(err?.error?.detail || 'Photo upload failed');
        this.photoUploading.set(false);
      },
    });
    input.value = '';
  }

  saveProfile() {
    if (this.profileForm.invalid || this.saving() || !this.hasProfileChanges()) return;
    this.saving.set(true);
    this.saveError.set(null);
    this.saveSuccess.set(false);

    const v = this.profileForm.getRawValue();
    const payload: UpdateMePayload = {
      first_name: v.first_name ?? undefined,
      last_name: v.last_name ?? undefined,
    };
    this.userService.updateMe(payload).subscribe({
      next: (user) => {
        this.auth.currentUser.set(user);
        this.saving.set(false);
        this.saveSuccess.set(true);
        setTimeout(() => this.saveSuccess.set(false), 3000);
      },
      error: (err) => {
        this.saveError.set(err?.error?.detail || 'Update failed');
        this.saving.set(false);
      },
    });
  }

  changePassword() {
    if (this.passwordForm.invalid || this.saving()) return;
    const { current_password, new_password, confirm_password } = this.passwordForm.value;
    if (new_password !== confirm_password) {
      this.passwordError.set('New passwords do not match');
      return;
    }

    this.saving.set(true);
    this.passwordError.set(null);
    this.passwordSuccess.set(false);

    const payload: ChangePasswordPayload = {
      current_password: current_password!,
      new_password: new_password!,
    };
    this.userService.changePassword(payload).subscribe({
      next: () => {
        this.saving.set(false);
        this.passwordSuccess.set(true);
        this.passwordForm.reset();
        this.showPasswordChange.set(false);
        setTimeout(() => this.passwordSuccess.set(false), 3000);
      },
      error: (err) => {
        this.passwordError.set(err?.error?.detail || 'Password change failed');
        this.saving.set(false);
      },
    });
  }

  menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { name: 'Applications', path: '/applications', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
    { name: 'Prompts', path: '/prompts', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
    { name: 'Agents', path: '/agents', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
    { name: 'Content Manager', path: '/content-manager', icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z' },
    { name: 'Image Generation', path: '/image-generation', icon: 'M4 7a2 2 0 012-2h3l1-1h4l1 1h3a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm8 3a4 4 0 100 8 4 4 0 000-8z' },
    { name: 'Image Library', path: '/image-library', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { name: 'Sound Library', path: '/audio-library', icon: 'M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z' },
    { name: 'Video Library', path: '/video-library', icon: 'M15 10l4.553-2.069A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
    { name: 'My Team', path: '/my-team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  ];

  /** Content editor cannot see other users; only super_admin and admin can. */
  canSeeOtherUsers(): boolean {
    const roles = this.auth.currentUser()?.roles as string[] | undefined;
    return !!roles && (roles.includes('super_admin') || roles.includes('admin'));
  }

  get visibleMenuItems() {
    const isAdmin = this.canSeeOtherUsers();
    return this.menuItems.filter(item => {
      if (item.name === 'My Team' || item.name === 'Agents') return isAdmin;
      return true;
    });
  }
}
